import React, {
  FocusEvent,
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  HTMLAttributes,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  Dayjs,
} from 'utils/dayjs';
import AutoCompletedInput from 'core/components/AutoComplete';
import {
  DEFAULT_DURATION_LIST,
  DurationOption,
  buildDurationOptions,
  formatEndTimeLabel,
  getEndTime,
  normalizeDurationOnBlur,
  parseDurationInput,
  toInputValue,
  toTypedDurationOption,
} from 'core/components/DateTimePicker/DurationPicker/utils';

type DurationPickerEndTimeAdornmentProps = {
  endTimeLabel: string;
  showEndTime: boolean;
};

type DurationPickerOptionRowProps = {
  optionProps: HTMLAttributes<HTMLLIElement>;
  option: DurationOption;
  endTimeLabel: string;
  selected: boolean;
  showSeparator: boolean;
};

type Props = {
  startTime: Dayjs | null;
  value: number;
  onChange: (minutes: number) => void;
  durationList?: number[];
  suggestedDurationInMinutes?: number;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
};

function DurationPickerEndTimeAdornment({
  endTimeLabel,
  showEndTime,
}: DurationPickerEndTimeAdornmentProps) {
  return (
    <div className="flex items-center gap-2 pl-2 ml-1 border-l border-gray-200 shrink-0">
      <span className={clsx('text-xs whitespace-nowrap', {
        'text-gray-500': showEndTime,
        'text-transparent': !showEndTime,
      })}
      >
        {showEndTime ? endTimeLabel : '0:00 am'}
      </span>
    </div>
  );
}

function DurationPickerOptionRow({
  optionProps,
  option,
  endTimeLabel,
  selected,
  showSeparator,
}: DurationPickerOptionRowProps) {
  return (
    <li
      {...optionProps}
      key={`${option.minutes}-${option.label}`}
      className={clsx(optionProps.className, {
        'border-b border-gray-200': showSeparator,
      })}
    >
      <div className="w-full flex items-center justify-between gap-4 px-1 py-0.5">
        <span className="text-xs font-medium flex-1">
          {option.label}
        </span>
        <span className="text-xs whitespace-nowrap">
          {endTimeLabel}
        </span>
      </div>
    </li>
  );
}

export default function DurationPicker({
  startTime,
  value,
  onChange,
  durationList = DEFAULT_DURATION_LIST,
  suggestedDurationInMinutes,
  className,
  disabled,
  placeholder = 'Duration',
}: Props) {
  const lastValidRef = useRef(value);
  const [inputValue, setInputValue] = useState(() => toInputValue(value));

  const typedOption = useMemo(
    () => toTypedDurationOption(inputValue),
    [inputValue],
  );

  const options = useMemo(
    () => buildDurationOptions(durationList, typedOption),
    [durationList, typedOption],
  );

  const endTime = useMemo(
    () => getEndTime(startTime, value),
    [startTime, value],
  );

  const endTimeLabel = useMemo(
    () => formatEndTimeLabel(endTime),
    [endTime],
  );

  const showEndTime = !isNil(endTime) && endTime.isValid();

  useEffect(() => {
    setInputValue(toInputValue(value));
    lastValidRef.current = value;
  }, [value]);

  const handleInputChange = (
    _event: SyntheticEvent,
    newInputValue: string,
    reason: string,
  ) => {
    if (reason === 'reset') {
      return;
    }

    setInputValue(
      reason === 'input'
        ? newInputValue.replace(/\D/g, '')
        : newInputValue,
    );
  };

  const handleBlur = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const raw = event.currentTarget?.value ?? inputValue;
    const normalized = normalizeDurationOnBlur(raw, lastValidRef.current);

    lastValidRef.current = normalized;
    setInputValue(toInputValue(normalized));
    onChange(normalized);
  };

  const handleSelect = (selected: DurationOption | DurationOption[] | string) => {
    const option = selected as DurationOption | string;
    const parsed = typeof option === 'string'
      ? parseDurationInput(option)
      : option.minutes;
    const minutes = isNil(parsed) || parsed <= 0
      ? lastValidRef.current
      : parsed;

    lastValidRef.current = minutes;
    setInputValue(toInputValue(minutes));
    onChange(minutes);
  };

  return (
    <AutoCompletedInput<DurationOption>
      freeSolo
      selectOnFocus
      options={options}
      filterOptions={(value) => value}
      filterSelectedOptions={false}
      value={typedOption}
      isOptionEqualToValue={(option, selected) => (
        option.minutes === (selected as DurationOption).minutes
      )}
      inputValue={inputValue}
      disabled={disabled}
      disableClearable
      openOnFocus
      popupIcon={null}
      className={clsx('duration-picker-autocomplete', className)}
      dropdownClassName="duration-picker-autocomplete-dropdown"
      placeholder={placeholder}
      onBlur={(event) => {
        handleBlur(event as FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      getLabel={(option) => (typeof option === 'string' ? option : option.label)}
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{
        endAdornment: (
          <DurationPickerEndTimeAdornment
            endTimeLabel={endTimeLabel}
            showEndTime={showEndTime}
          />
        ),
      }}
      renderOption={(optionProps, option) => (
        <DurationPickerOptionRow
          optionProps={optionProps}
          option={option}
          endTimeLabel={formatEndTimeLabel(getEndTime(startTime, option.minutes))}
          selected={option.minutes === value}
          showSeparator={
            !isNil(suggestedDurationInMinutes)
            && option.minutes === suggestedDurationInMinutes
          }
        />
      )}
    />
  );
}
